/* eslint-disable react/no-unstable-nested-components */
import { ResponsiveBar } from '@nivo/bar'
import PropTypes from 'prop-types'
import React from 'react'

import { MetricCard } from '@/components/shared/Metrics'
import colors from '@/utils/colors'
import { formatCurrency } from '@/utils/formatters'

/**
 *
 * Highlights
 *
 */
const Highlights = ({ kpis, highDollarThreshold = null, loading = false }) => (
  <div className="text-white-light flex w-full flex-col gap-4">
    <div className="grid w-full grid-cols-2 grid-rows-5 gap-4 sm:grid-cols-2 sm:grid-rows-3 md:grid-cols-4 md:grid-rows-2">
      <MetricCard
        label="Total Active Repair Orders"
        value={kpis?.numberOfOpenRos}
        loading={loading}
        skeletonWidth="8"
      />

      <MetricCard
        label="Total Active Amount"
        value={Number(kpis?.totalUnresolvedClaimAmount || 0).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        loading={loading}
        skeletonWidth="12"
        defaultValue="$0"
      />

      <div className="bg-midnight col-span-2 row-span-3 flex flex-col items-center rounded-lg p-4 sm:col-span-2 md:col-span-2 md:row-span-2">
        <span className="text-brownGray text-xs uppercase leading-4 tracking-[1.5px]">
          RO Age Histogram
        </span>

        <div className="h-full w-full md:h-32">
          <ResponsiveBar
            borderRadius="4px"
            data={kpis?.roAgeHistogram || []}
            keys={['age']}
            indexBy="days"
            margin={{ top: 5, right: 0, bottom: 20, left: 25 }}
            padding={0.4}
            indexScale={{ type: 'band', round: true }}
            valueScale={{ type: 'linear' }}
            colors={colors.gold.DEFAULT}
            enableLabel={false}
            layers={['axes', 'bars']}
            axisBottom={{
              tickSize: 0,
              tickPadding: 5,
              tickRotation: 0,
            }}
            axisTop={null}
            axisRight={null}
            axisLeft={{
              tickSize: 0,
              tickPadding: 5,
              tickRotation: 0,
              tickValues: 5,
              format: (e) => (Math.floor(e) === e ? e : ''),
            }}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fill: colors.brownGray.DEFAULT,
                    fontSize: '12px',
                    fontWeight: '500',
                  },
                },
              },
            }}
            tooltip={(point) => (
              <div
                style={{
                  padding: '4px 5px',
                  background: '#F0F0ED',
                  borderRadius: '8px',
                  border: '1px solid rgb(255, 255, 255)',
                  boxShadow: 'rgba(0, 0, 0, 0.25) 0px 4px 10px',
                  fontSize: '15px',
                  color: '#045485',
                }}
              >
                <span>RO Count: {point.value}</span>
              </div>
            )}
          />
        </div>
      </div>

      <MetricCard
        label="New in Last 7 Days"
        value={kpis?.newInLast7Days}
        loading={loading}
        skeletonWidth="8"
      />

      <MetricCard
        label="Resolved in Last 7 Days"
        value={kpis?.resolvedInLast7Days}
        loading={loading}
        skeletonWidth="8"
      />

      <MetricCard
        label="Number of High Dollar"
        value={kpis?.numberOfHighDollarClaims}
        loading={loading}
        size="small"
        skeletonWidth="4"
      >
        <MetricCard.Tooltip
          content={
            highDollarThreshold ? (
              <span>
                The high-dollar threshold for this dealer is{' '}
                <span>{formatCurrency(highDollarThreshold)}</span>
              </span>
            ) : (
              <span>The high-dollar threshold is unique per manufacturer.</span>
            )
          }
        />
      </MetricCard>

      <MetricCard
        label="No update in more than 5 days"
        value={kpis?.noUpdateInMoreThan5Days}
        loading={loading}
        skeletonWidth="4"
        size="small"
      />

      <MetricCard
        label="Aging out in next 7 days"
        value={Number(kpis?.agingOutInNext7DaysAmount || 0).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        loading={loading}
        skeletonWidth="12"
        defaultValue="$0"
        size="small"
      />

      <MetricCard
        label="Aged Out"
        value={Number(kpis?.agedOutRosAmount || 0).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        loading={loading}
        skeletonWidth="12"
        defaultValue="$0"
        size="small"
      />
    </div>
  </div>
)

Highlights.propTypes = {
  kpis: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  highDollarThreshold: PropTypes.number,
  loading: PropTypes.bool,
}

export default Highlights
